import { SpaceProvider } from '@ably/spaces/react';
import { DndContext } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useContext, useMemo, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import EncounterChecklistChannel from '@/channels/EncounterChecklistChannel';
import { encounterChecklistSpaceName } from '@/channels/utils/channelNames';
import AiProfileShortcuts from '@/components/AiProfileShortcuts';
import CardOrderDraftBar from '@/components/CardOrderDraftBar';
import CompactTaskList from '@/components/CompactTaskList';
import EpisodeOfCareSection from '@/components/EpisodeOfCareSection';
import MostRecentChallengeResponse from '@/components/MostRecentChallengeResponse';
import PersonAvailabilitySummary from '@/components/PersonAvailabilitySummary';
import PersonCareTeam from '@/components/PersonCareTeam';
import PersonMedicationsSummary from '@/components/PersonMedicationsSummary';
import PersonRecentContact from '@/components/PersonRecentContact';
import PersonRecoveryGoal from '@/components/PersonRecoveryGoal';
import PersonTags, { hasTags } from '@/components/PersonTags';
import ScheduleSummary from '@/components/ScheduleSummary';
import { taskable_type_person } from '@/constants/taskableTypes';
import AppContext from '@/contexts/appContext';
import { useAttendanceChecklist } from '@/hooks/useEncounters';
import usePageCardOrder from '@/hooks/usePageCardOrder';
import PersonContext from '@/pages/Person/PersonContext';
import Banners from '@/pages/Profile/components/Banners';
import HiddenCardsDivider from '@/pages/Profile/components/HiddenCardsDivider';
import HideCardModal from '@/pages/Profile/components/HideCardModal';
import SortableSection from '@/pages/Profile/components/SortableSection';
import { ProfileSection } from '@/pages/Profile/types';
import { ENCOUNTER_TYPES, type Encounter } from '@/types/encounter';
import { SeeAllLink, Typography } from '@/ui';
import { hasPermission, PEOPLE_VIEW_AI_PROFILE } from '@/utils/userPermission';

import EncounterCharts from '../BillingItem/components/EncounterCharts';
import EncounterBillingCard from '../EncounterBillingCard';
import EncounterChecklistCard from '../EncounterChecklistCard';
import EncounterRefreshButton from '../EncounterRefreshButton';

import { DrugScreenSection, hasDrugScreenCard } from './components';
import { EncounterSection, type EncounterSectionCard, type Props } from './types';

const EncounterPersonDetails = ({ billingItem = null, encounterType }: Props) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const personContext = useContext(PersonContext);
  const { permissions } = useContext(AppContext);
  const outletContext = useOutletContext<{ encounter?: Encounter } | null>();
  const encounter = outletContext?.encounter;

  const enableAiProfile = useMemo(
    () => hasPermission({ permissions, requiredPermission: PEOPLE_VIEW_AI_PROFILE }),
    [permissions]
  );

  const person = personContext?.person;
  const personId = person?.id;

  // Only a member's attendance has a checklist. Its own card asks for the same
  // thing, and the cache answers both, so the count can sit on the heading row.
  const isAttendance = encounterType === ENCOUNTER_TYPES.APPOINTMENT_ATTENDANCE;
  const { data: checklist = [] } = useAttendanceChecklist({
    enabled: isAttendance && !!encounterId,
    encounterId
  });

  const taskables = useMemo(
    () => [
      ...(personId ? [{ contentTypeName: taskable_type_person, id: personId }] : []),
      ...(encounter?.encounter_id && encounter?.encounter_type
        ? [{ contentTypeName: encounter.encounter_type, id: encounter.encounter_id }]
        : [])
    ],
    [encounter?.encounter_id, encounter?.encounter_type, personId]
  );

  /*
   * The cards this view is made of, in the order a counselor who has never
   * arranged it sees them. Billing starts closed.
   *
   * A card the member has nothing for marks itself unavailable and is left off
   * the page, as the profile's are, since the section around it draws a heading
   * before the card runs and would otherwise leave a title over an empty card.
   */
  const sectionCards: EncounterSectionCard[] = [
    {
      actions: <SeeAllLink label="View all tasks" to={`/people/${personId}/tasks`} />,
      defaultOpen: true,
      id: EncounterSection.TASKS,
      render: () => <CompactTaskList personId={personId as string} taskables={taskables} />,
      title: 'Tasks'
    },
    {
      defaultOpen: true,
      id: EncounterSection.TAGS,
      isUnavailable: !hasTags(person),
      render: () => <PersonTags allowEdit={false} />,
      title: 'Tags'
    },
    {
      defaultOpen: true,
      id: EncounterSection.GOAL,
      render: () => <PersonRecoveryGoal />,
      title: 'Goal'
    },
    {
      actions: encounterId ? (
        <div className="flex items-center gap-2">
          <EncounterRefreshButton
            encounterId={encounterId}
            cachedAt={billingItem?.cached_at}
            hasBillingItem={!!billingItem}
          />
          {!!checklist.length && (
            <Typography muted variant="caption">
              {checklist.filter(({ is_complete }) => is_complete === true).length} of{' '}
              {checklist.length}
            </Typography>
          )}
        </div>
      ) : undefined,
      defaultOpen: true,
      id: EncounterSection.CHECKLIST,
      isUnavailable: !isAttendance || !encounterId || !personId,
      render: () =>
        encounterId && personId ? (
          <EncounterChecklistChannel personId={personId}>
            <SpaceProvider name={encounterChecklistSpaceName(personId)}>
              <EncounterChecklistCard
                encounter={encounter as Encounter}
                encounterId={encounterId}
                personId={personId}
              />
            </SpaceProvider>
          </EncounterChecklistChannel>
        ) : null,
      title: 'Clinical Reminders'
    },
    {
      actions: <SeeAllLink label="Edit" to={`/people/${personId}#${ProfileSection.CARE_TEAM}`} />,
      defaultOpen: true,
      id: EncounterSection.CARE_TEAM,
      render: () => <PersonCareTeam />,
      title: 'Care Team'
    },
    {
      actions: <SeeAllLink to={`/people/${personId}/medications`} />,
      defaultOpen: true,
      id: EncounterSection.MEDICATIONS,
      render: () => <PersonMedicationsSummary />,
      title: 'Medications'
    },
    {
      actions: <SeeAllLink label="Edit" to={`/people/${personId}/availability`} />,
      defaultOpen: true,
      id: EncounterSection.AVAILABILITY,
      render: () => <PersonAvailabilitySummary />,
      title: 'Member Availability'
    },
    {
      defaultOpen: true,
      id: EncounterSection.DRUG_SCREEN,
      isUnavailable: !hasDrugScreenCard(encounter),
      render: () => <DrugScreenSection />,
      title: 'Drug Screen'
    },
    {
      actions: <SeeAllLink to={`/people/${personId}/challenges`} />,
      defaultOpen: true,
      id: EncounterSection.CHALLENGE_RESPONSE,
      render: () => <MostRecentChallengeResponse personId={personId as string} />,
      title: 'Last Challenge Response'
    },
    {
      actions: <SeeAllLink to={`/people/${personId}/messaging`} />,
      defaultOpen: true,
      id: EncounterSection.RECENT_CONTACT,
      render: () => <PersonRecentContact />,
      title: 'Recent Contact'
    },
    {
      actions: <SeeAllLink label="Show appointments" to={`/people/${personId}/appointments`} />,
      defaultOpen: true,
      id: EncounterSection.SCHEDULE,
      render: () => <ScheduleSummary />,
      title: 'Upcoming Appointments'
    },
    {
      defaultOpen: true,
      id: EncounterSection.EPISODE_OF_CARE,
      render: () => <EpisodeOfCareSection showTitle={false} />,
      title: 'Episode of Care'
    },
    {
      defaultOpen: true,
      id: EncounterSection.ASK_AI,
      isUnavailable: !enableAiProfile,
      render: () => (
        <div className="mt-2 flex flex-wrap gap-1">
          <AiProfileShortcuts personId={personId as string} />
        </div>
      ),
      title: 'Ask AI'
    },
    {
      // The refresh only exists for a member's attendance.
      actions:
        encounterId && encounterType === ENCOUNTER_TYPES.APPOINTMENT_ATTENDANCE ? (
          <EncounterRefreshButton
            encounterId={encounterId}
            cachedAt={billingItem?.cached_at}
            hasBillingItem={!!billingItem}
          />
        ) : undefined,
      defaultOpen: false,
      id: EncounterSection.BILLING,
      isUnavailable: !encounterType,
      render: () =>
        encounterType ? (
          <EncounterBillingCard billingItem={billingItem} encounterType={encounterType} />
        ) : null,
      title: 'Billing'
    },
    {
      defaultOpen: true,
      id: EncounterSection.CHARTS,
      render: () => <EncounterCharts charts={encounter?.charts ?? []} showTitle={false} />,
      title: 'Charts'
    }
  ];

  const {
    cards: sections,
    draftBar,
    onDragEnd,
    onHide,
    onToggle,
    onUnhide
  } = usePageCardOrder<EncounterSectionCard>({
    cards: sectionCards,
    settingKey: 'encounterSections'
  });

  const [sectionToHide, setSectionToHide] = useState<EncounterSectionCard | null>(null);
  const [showHiddenSections, setShowHiddenSections] = useState(false);

  const availableSections = sections.filter(({ isUnavailable }) => !isUnavailable);
  const shownSections = availableSections.filter(({ hidden }) => !hidden);
  const hiddenSections = availableSections.filter(({ hidden }) => hidden);

  const confirmHideSection = () => {
    if (sectionToHide) onHide(sectionToHide.id);
    setSectionToHide(null);
  };

  if (!personId) return <div>No Person found on encounter.</div>;

  return (
    <>
      <Banners person={personContext.person} />

      <CardOrderDraftBar {...draftBar} />

      <DndContext onDragEnd={onDragEnd}>
        <SortableContext items={shownSections} strategy={verticalListSortingStrategy}>
          {shownSections.map((section) => (
            <SortableSection
              actions={section.actions}
              key={section.id}
              onClick={onToggle}
              onHide={(sectionId) =>
                setSectionToHide(shownSections.find(({ id }) => id === sectionId) ?? null)
              }
              section={section}>
              {section.render()}
            </SortableSection>
          ))}
        </SortableContext>

        {/* Hidden cards stay out of the sortable list: they always sit at the bottom. */}
        {hiddenSections.length > 0 && (
          <SortableContext items={hiddenSections}>
            <HiddenCardsDivider
              count={hiddenSections.length}
              isOpen={showHiddenSections}
              onToggle={() => setShowHiddenSections((open) => !open)}
            />

            {showHiddenSections &&
              hiddenSections.map((section) => (
                <SortableSection
                  key={section.id}
                  onClick={onToggle}
                  onUnhide={onUnhide}
                  section={section}>
                  {section.render()}
                </SortableSection>
              ))}
          </SortableContext>
        )}
      </DndContext>

      <HideCardModal
        cardTitle={sectionToHide?.title ?? null}
        editingRoleName={draftBar.editingRoleName}
        onClose={() => setSectionToHide(null)}
        onConfirm={confirmHideSection}
      />
    </>
  );
};

export default EncounterPersonDetails;
